<template>
  <div class="wysiwyg editor">
    <editor-menu-bar
      :editor="editor"
      v-slot="{
        commands,
        isActive,
        getMarkAttrs,
      }">
      <div class="menubar mb-3">
        <!-- Heading Level 1 -->
        <v-btn
          tabindex="-1"
          title="Heading 1"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) && !isHTMLEditor }"
          @click="commands.heading({ level: 1 })"
          :disabled="isHTMLEditor"
          text>
          H1
        </v-btn>
        <!-- / Heading Level 1 -->

        <!-- Heading 2 -->
        <v-btn
          tabindex="-1"
          title="Heading 2"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) && !isHTMLEditor }"
          @click="commands.heading({ level: 2 })"
          :disabled="isHTMLEditor"
          text>
          H2
        </v-btn>
        <!-- / Heading 2 -->

        <!-- Heading 3 -->
        <v-btn
          tabindex="-1"
          title="Heading 3"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) && !isHTMLEditor }"
          @click="commands.heading({ level: 3 })"
          :disabled="isHTMLEditor"
          text>
          H3
        </v-btn>
        <!-- / Heading 3 -->

        <!-- Heading 4 -->
        <v-btn
          tabindex="-1"
          title="Heading 4"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 4 }) && !isHTMLEditor }"
          @click="commands.heading({ level: 4 })"
          :disabled="isHTMLEditor"
          text>
          H4
        </v-btn>
        <!-- / Heading 4 -->

        <!-- Font Size -->
        <v-menu
          offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              tabindex="-1"
              title="Font Size"
              class="menubar__button"
              :class="{ 'is-active': isActive.font_size() && !isHTMLEditor }"
              v-on="on"
              :disabled="isHTMLEditor"
              text>
              <v-icon>mdi-format-size</v-icon>
            </v-btn>
          </template>
          <v-list class="compact">
            <v-list-item
              v-for="fontSize in fontSizes"
              :key="fontSize"
              @click="commands.font_size({ fontSize: `${fontSize}px` })">
              <v-list-item-title>{{ fontSize }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- / Font Size -->

        <!-- Highlight Color -->
        <v-menu
          :close-on-content-click="false"
          offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              tabindex="-1"
              title="Highlight Color"
              class="menubar__button"
              :class="{ 'is-active': isActive.background_color() && !isHTMLEditor }"
              v-on="on"
              :disabled="isHTMLEditor"
              text>
              <v-icon>mdi-format-color-fill</v-icon>
            </v-btn>
          </template>
          <v-color-picker
            @input="commands.background_color({ backgroundColor: $event.hex })">
          </v-color-picker>
        </v-menu>
        <!-- / Highlight Color -->

        <!-- Font Color -->
        <v-menu
          :close-on-content-click="false"
          offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              tabindex="-1"
              title="Font Color"
              class="menubar__button"
              :class="{ 'is-active': isActive.color() && !isHTMLEditor }"
              v-on="on"
              :disabled="isHTMLEditor"
              text>
              <v-icon>mdi-format-color-text</v-icon>
            </v-btn>
          </template>
          <v-color-picker
            @input="commands.color({ color: $event.hex })">
          </v-color-picker>
        </v-menu>
        <!-- / Font Color -->

        <!-- Bold -->
        <v-btn
          tabindex="-1"
          title="Bold"
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() && !isHTMLEditor }"
          @click="commands.bold"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <!-- / Bold -->

        <!-- Italic -->
        <v-btn
          tabindex="-1"
          title="Italic"
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() && !isHTMLEditor }"
          @click="commands.italic"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <!-- / Italic -->

        <!-- Underline -->
        <v-btn
          tabindex="-1"
          title="Underline"
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() && !isHTMLEditor }"
          @click="commands.underline"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
        <!-- / Underline -->

        <!-- Bullet List -->
        <v-btn
          tabindex="-1"
          title="Bullet List"
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() && !isHTMLEditor }"
          @click="commands.bullet_list"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <!-- / Bullet List -->

        <!-- Numbered List -->
        <v-btn
          tabindex="-1"
          title="Numbered List"
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() && !isHTMLEditor }"
          @click="commands.ordered_list"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>
        <!-- / Numbered List -->

        <!-- Undo -->
        <v-btn
          tabindex="-1"
          title="Undo"
          class="menubar__button"
          @click="commands.undo"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-undo</v-icon>
        </v-btn>
        <!-- / Undo -->

        <!-- Redo -->
        <v-btn
          tabindex="-1"
          title="Redo"
          class="menubar__button"
          @click="commands.redo"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-redo</v-icon>
        </v-btn>
        <!-- / Redo -->

        <!-- Heading Alignment -->
        <span
          v-if="isActive.heading()">
            <v-btn
            tabindex="-1"
            title="Align Left"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ align: 'left' }) && !isHTMLEditor }"
            @click="commands.heading({ align: 'left' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-left</v-icon>
          </v-btn>

          <v-btn
            tabindex="-1"
            title="Align Center"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ align: 'center' }) && !isHTMLEditor }"
            @click="commands.heading({ align: 'center' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-center</v-icon>
          </v-btn>

          <v-btn
            tabindex="-1"
            title="Align Right"
            class="menubar__button"
            :class="{ 'is-active': isActive.heading({ align: 'right' }) && !isHTMLEditor }"
            @click="commands.heading({ align: 'right' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-right</v-icon>
          </v-btn>
        </span>
        <!-- / Heading Alignment -->

        <!-- Paragraph Alignment -->
        <span
          v-if="isActive.paragraph()">
            <v-btn
            tabindex="-1"
            title="Align Left"
            class="menubar__button"
            :class="{ 'is-active': isActive.paragraph({ align: 'left' }) && !isHTMLEditor }"
            @click="commands.paragraph({ align: 'left' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-left</v-icon>
          </v-btn>

          <v-btn
            tabindex="-1"
            title="Align Center"
            class="menubar__button"
            :class="{ 'is-active': isActive.paragraph({ align: 'center' }) && !isHTMLEditor }"
            @click="commands.paragraph({ align: 'center' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-center</v-icon>
          </v-btn>

          <v-btn
            tabindex="-1"
            title="Align Right"
            class="menubar__button"
            :class="{ 'is-active': isActive.paragraph({ align: 'right' }) && !isHTMLEditor }"
            @click="commands.paragraph({ align: 'right' })"
            :disabled="isHTMLEditor"
            text>
            <v-icon>mdi-format-align-right</v-icon>
          </v-btn>
        </span>
        <!-- / Paragraph Alignment -->

        <!-- Edit HTML Code -->
        <v-btn
          tabindex="-1"
          title="Edit HTML"
          class="menubar__button"
          :class="{ 'is-active': isHTMLEditor }"
          @click="isHTMLEditor = !isHTMLEditor;"
          text>
          <v-icon>mdi-xml</v-icon>
        </v-btn>
        <!-- / Edit HTML Code -->

        <!-- Horizontal Rule -->
        <v-btn
          tabindex="-1"
          title="Horizontal Rule"
          class="menubar__button"
          :class="{ 'is-active': isActive.horizontal_rule() && !isHTMLEditor }"
          @click="commands.horizontal_rule"
          :disabled="isHTMLEditor"
          text>
          <v-icon>mdi-format-page-break</v-icon>
        </v-btn>
        <!-- / Horizontal Rule -->

        <!-- Link -->
        <v-menu
          v-model="linkMenuOpen"
          @input="toggleLinkMenu(getMarkAttrs('link'))"
          :close-on-content-click="false"
          offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              tabindex="-1"
              title="Link"
              class="menubar__button"
              v-on="on"
              :class="{ 'is-active': isActive.link() && !isHTMLEditor }"
              :disabled="isHTMLEditor"
              text>
              <v-icon>mdi-link-variant</v-icon>
            </v-btn>
          </template>
          <form
            id="linkForm"
            class="d-flex white py-2 px-2"
            @submit.prevent="setLinkUrl(commands.link, linkUrl)">
            <v-text-field
              v-model="linkUrl"
              placeholder="https://"
              ref="linkInput"
              outlined
              filled
              hide-details>
            </v-text-field>

            <v-btn
              class="shrink"
              title="Remove Link"
              @click="setLinkUrl(commands.link, '')"
              type="button"
              :disabled="!linkUrl"
              text>
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </form>
        </v-menu>
        <!-- / Link -->

        <v-divider></v-divider>
      </div>
    </editor-menu-bar>

    <!-- Editor -->
    <editor-content
      v-if="!isHTMLEditor"
      class="editor__content"
      :editor="editor"
    />
    <!-- / Editor -->

    <!-- HTML Editor -->
    <ContentEditable
      v-else
      v-model="htmlContent"
    />
    <!-- / HTML Editor -->

    <!-- Word & Character Count -->
    <div class="mt-2">
      <v-chip
        class="mr-2"
        label>
        Words: {{wordCount}}
      </v-chip>

      <v-chip
        label>
        Characters: {{characterCount}}
      </v-chip>
    </div>
    <!-- / Word & Character Count -->
  </div>
</template>

<script>
import {
  Editor,
  EditorContent,
  EditorMenuBar,
} from 'tiptap';

import {
  BulletList,
  CodeBlock,
  HardBreak,
  OrderedList,
  ListItem,
  Bold,
  Italic,
  Underline,
  History,
  Image,
  HorizontalRule,
} from 'tiptap-extensions';

import ContentEditable from '../ContentEditable.vue';
import TargetBlankLink from './TargetBlankLink';
import Heading from './Heading';
import Paragraph from './Paragraph';
import FontColor from './FontColor';
import BackgroundColor from './BackgroundColor';
import FontSize from './FontSize';

let cancelValueWatch;

export default {
  name: 'WYSIWYG',
  props: ['value', 'readonly'],
  components: {
    EditorContent,
    EditorMenuBar,
    ContentEditable,
  },
  data() {
    return {
      editor: null,
      linkUrl: '',
      linkMenuOpen: false,
      isHTMLEditor: false,
      fontSizes: [
        8,
        9,
        10,
        11,
        12,
        14,
        16,
        18,
        24,
        30,
        36,
        48,
        60,
        72,
        96,
      ],
    };
  },
  watch: {
    readonly(readonly) {
      this.editor.setOptions({ editable: !readonly });
    },
  },
  computed: {
    htmlContent: {
      get() {
        return this.value;
      },
      set(htmlContent) {
        this.$emit('input', htmlContent);
      },
    },
    textContent() {
      let textContent = document.createElement('span');
      textContent.innerHTML = this.value;
      textContent = textContent.textContent || textContent.innerText;
      return textContent;
    },
    wordCount() {
      return this.textContent ? this.textContent.split(/\W+/).length : 0;
    },
    characterCount() {
      return this.textContent.length;
    },
  },
  methods: {
    startValueWatch() {
      cancelValueWatch = this.$watch('value', (value) => {
        this.editor.setContent(value || '', false, 'full');
      });
    },
    stopValueWatch() {
      cancelValueWatch();
    },
    toggleLinkMenu({ href }) {
      if (this.linkMenuOpen) {
        this.linkUrl = href;
        setTimeout(() => {
          this.$refs.linkInput.focus();
        }, 200);
      } else {
        setTimeout(() => {
          this.linkUrl = '';
        }, 200);
      }
    },
    setLinkUrl(command, url) {
      this.linkUrl = url;
      command({ href: url });
      this.linkMenuOpen = false;
    },
  },
  created() {
    this.startValueWatch();
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        new BulletList(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3, 4] }),
        new ListItem(),
        new OrderedList(),
        new TargetBlankLink(),
        new Bold(),
        new Italic(),
        new Underline(),
        new History(),
        new Image(),
        new Paragraph(),
        new FontColor(),
        new BackgroundColor(),
        new FontSize(),
        new HorizontalRule(),
      ],
      onUpdate: ({ getHTML }) => {
        let input = getHTML();

        if (input === '<p></p>') {
          input = '';
        }

        this.stopValueWatch();
        this.$emit('input', input);
        this.$nextTick(this.startValueWatch);
      },
      content: this.value,
      editable: !this.readonly,
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
#linkForm {
  height: 46px;
  .v-input {
    width: 300px;
    margin-right: 8px;
  }
}

.editor {
  position: relative;
  overflow: hidden;

  .menubar__button {
    min-width: 0 !important;
    padding: 0 8px !important;
    border-radius: 0;

    &.is-active {
      background: #ccc;
    }
  }

  .editor__content {
    .ProseMirror {
      min-height: 100px;
      padding-bottom: 1rem;
      outline: none;
      font-size: 16px;

      p {
        margin-bottom: 0;
      }

      blockquote {
        padding: 16px 0 16px 24px;
        font-size: 18px;
        font-weight: 300;
      }

      &::before {
        border-color: rgba(0, 0, 0, 0.42);
        border-style: solid;
        border-width: thin 0 0 0;
        bottom: 0;
        content: "";
        left: 0;
        position: absolute;
        -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        width: 100%;
      }

      &::after {
        border-color: #2d48c5;
        border-style: solid;
        border-width: thin 0 thin 0;
        -webkit-transform: scaleX(0);
        transform: scaleX(0);
        bottom: 0;
        content: "";
        left: 0;
        position: absolute;
        -webkit-transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        width: 100%;
      }

      &.ProseMirror-focused {
        &::before {
          border-color: rgba(0, 0, 0, 0.87);
        }

        &::after {
          -webkit-transform: scaleX(1);
          transform: scaleX(1);
        }
      }
    }
  }
}
</style>
